import React, { useState, useEffect } from 'react';
import { useAuth, api } from '../contexts/AuthContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Save, UserPlus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const industries = ['Construction & Trades', 'Manufacturing', 'Healthcare', 'Hospitality', 'Retail', 'Logistics & Transport', 'Agriculture', 'Mining', 'Professional Services', 'Education', 'Technology', 'Other'];

export default function SettingsPage() {
  const { user } = useAuth();
  const [settings, setSettings] = useState({ company: null, team: [] });
  const [companyName, setCompanyName] = useState('');
  const [industry, setIndustry] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try { const { data } = await api.get(`/api/settings`); setSettings(data); if (data.company) { setCompanyName(data.company.name || ''); setIndustry(data.company.industry || ''); } }
    catch (err) { console.error(err); } finally { setLoading(false); }
  };

  const handleSave = async () => { setSaving(true); try { await api.put(`/api/settings`, { company_name: companyName, industry }); toast.success('Saved'); } catch { toast.error('Failed'); } finally { setSaving(false); } };

  const handleInvite = async (e) => {
    e.preventDefault(); if (!inviteEmail.trim()) return;
    try { const fd = new FormData(); fd.append('email', inviteEmail); await api.post(`/api/settings/invite`, fd); toast.success(`Invited ${inviteEmail}`); setInviteEmail(''); fetchSettings(); }
    catch { toast.error('Failed'); }
  };

  if (loading) return <div className="max-w-3xl mx-auto animate-pulse space-y-6"><div className="h-8 bg-[#1F2937] rounded w-1/3" /><div className="bg-[#111827] rounded-lg border border-[#374151] p-6 space-y-4"><div className="h-10 bg-[#1F2937] rounded" /></div></div>;

  return (
    <div className="max-w-3xl mx-auto space-y-8" data-testid="settings-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold text-white tracking-tight">Settings</h1>
        <p className="text-[#9CA3AF] mt-1">Manage your company and team.</p>
      </div>

      {/* Company */}
      <div className="bg-[#111827] rounded-lg border border-[#374151] p-6">
        <h2 className="text-lg font-medium text-[#AAFF00] mb-4">Company</h2>
        <div className="space-y-4">
          <div>
            <Label className="text-[#F9FAFB]">Company Name</Label>
            <Input value={companyName} onChange={(e) => setCompanyName(e.target.value)} className="mt-1.5 bg-[#1F2937] border-[#374151] text-[#F9FAFB]" data-testid="settings-company-name" />
          </div>
          <div>
            <Label className="text-[#F9FAFB]">Industry</Label>
            <Select value={industry} onValueChange={setIndustry}>
              <SelectTrigger className="mt-1.5 bg-[#1F2937] border-[#374151] text-[#F9FAFB]" data-testid="settings-industry-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#111827] border-[#374151]">{industries.map(ind => <SelectItem key={ind} value={ind} className="text-[#F9FAFB] focus:bg-[#1F2937]">{ind}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button onClick={handleSave} disabled={saving} className="bg-[#AAFF00] text-[#080812] hover:bg-[#c4ff4d] gap-2 font-semibold" data-testid="settings-save-btn"><Save size={16} /> {saving ? 'Saving...' : 'Save Changes'}</Button>
        </div>
      </div>

      {/* Team */}
      <div className="bg-[#111827] rounded-lg border border-[#374151] p-6">
        <h2 className="text-lg font-medium text-[#AAFF00] mb-4">Team Members</h2>
        <form onSubmit={handleInvite} className="flex gap-2 mb-4">
          <Input value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="colleague@company.com" type="email" className="flex-1 bg-[#1F2937] border-[#374151] text-[#F9FAFB] placeholder:text-[#9CA3AF]" data-testid="invite-email-input" />
          <Button type="submit" variant="outline" className="gap-2 border-[#374151] text-[#9CA3AF] hover:bg-[#1F2937] hover:text-[#F9FAFB]" data-testid="invite-btn"><UserPlus size={16} /> Invite</Button>
        </form>
        <div className="divide-y divide-[#374151]">
          {settings.team.map((member, i) => (
            <div key={i} className="flex items-center justify-between py-3">
              <div><p className="text-sm font-medium text-[#F9FAFB]">{member.full_name || member.email}</p><p className="text-xs text-[#9CA3AF]">{member.email} &middot; {member.role}</p></div>
              <span className="text-xs px-2 py-1 bg-[#1F2937] rounded text-[#9CA3AF] border border-[#374151]">{member.role}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Danger */}
      <div className="bg-[#111827] rounded-lg border border-red-500/30 p-6">
        <h2 className="text-lg font-medium text-red-400 mb-2">Danger Zone</h2>
        <p className="text-sm text-[#9CA3AF] mb-4">Permanently delete your company and all associated data.</p>
        <Button variant="outline" className="text-red-400 border-red-500/30 hover:bg-red-500/10 gap-2" data-testid="delete-company-btn"><Trash2 size={16} /> Delete Company</Button>
      </div>
    </div>
  );
}
